import React from 'react';
import { Link } from 'react-router-dom';

import { Field } from 'formik';
import * as Yup from 'yup';

import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { phoneRegexp } from '@common/react/utils/validationHelpers';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import {
	Ailments,
	FormResponse,
	FormValues,
	PetSpecies,
	Sources,
} from '@app/components/Pages/Area/AreaObjects';
import { pushEvent } from '@app/components/TagManager';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomReCAPTCHA from '@app/components/UI/CustomReCAPTCHA/CustomReCAPTCHA';
import { CustomFile } from '@app/components/UI/Forms/CustomFile/CustomFile';
import FormikField from '@app/components/UI/Forms/CustomFormikField/CustomFormikField';
import { CustomPhoneControl } from '@app/components/UI/Forms/CustomPhoneControl/CustomPhoneControl';
import CustomSelect from '@app/components/UI/Forms/CustomSelect/CustomSelect';
import SearchFieldAuto from '@app/components/UI/Forms/SearchField/SearchFieldAuto';
import MiddleText from '@app/components/UI/MiddleText/MiddleText';
import { PhoneProviderContext } from '@app/providers/PhoneProvider/PhoneProvider';
import { Area } from '@app/objects/Area';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import '@app/scss/localCommon/components/attachment.scss';
import { stateFilter } from '@app/utils/StateFilter';
import { numberBeautifyValidator, validateRequiredString, zipCodeValidator } from '@app/utils/Validators';
import { onFileUpload } from '@app/utils/onFileUpload';
import { transformStateList } from '@app/utils/transformList';

const initialValues: FormValues = {
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	streetLine1: '',
	streetLine2: '',
	city: '',
	state: '',
	zip: '',
	source: null,
	petName: '',
	petSpecies: null,
	gender: null,
	petAge: null,
	petWeight: null,
	ailment: null,
	aftercare: null,
	comment: '',
	filePath: [],
	areaId: -1,
	captcha: '',
	id: -1,
};

const validationSchema = Yup.object({
	firstName: validateRequiredString('First Name is a required field'),
	lastName: validateRequiredString('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	phone: Yup.string().required('Phone Number is a required field').matches(phoneRegexp, 'Invalid Phone Number'),
	streetLine1: validateRequiredString('Street Address is a required field'),
	city: validateRequiredString('City is a required field'),
	state: validateRequiredString('State is a required field'),
	zip: zipCodeValidator(),
	gender: Yup.number().required("Pet's Gender is a required field").nullable(),
	petName: validateRequiredString("Pet's Name is a required field"),
	petAge: numberBeautifyValidator(
		"Pet's Age must be a number",
		"Pet's Age must be greater than zero",
		"Pet's Age is a required field",
		"Pet's Age must be a whole number",
	),
	petWeight: numberBeautifyValidator(
		"Pet's Weight must be a number",
		"Pet's Weight must be greater than zero",
		"Pet's Weight is a required field",
		"Pet's Weight must be a whole number (1lb or more, with no decimals)",
	),
	petSpecies: Yup.string().nullable().required("Pet's Species is a required field"),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const FormSuccess: React.FC = () => {
	const contextPhone = React.useContext(PhoneProviderContext);

	if (!contextPhone?.state) throw new Error('Error PhoneProvider context');

	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="area-request-form__success">
			<div className="area-request-form__success-checkmark">
				<img src={require('@app/images/checkmark_green.svg')} alt="success" />
			</div>

			<h3>We're honored to help</h3>
			<h5>
				A Veterinary Care Coordinator will reach out to you from our support number&nbsp;
				<a className="get-in-touch-info__phone" href={contextPhone.state.phoneNumberHref}>
					<span>{contextPhone.state.phoneNumberDisplay}</span>
				</a>
				.
			</h5>
			<h5>If you need more immediate assistance, you can call us 7 days a week.</h5>

			<h4>Visit the Lap of Love Resource Center</h4>
			<p>All information you could ever need about geriatric pets and how to care for them.</p>
			<Link className=" btn btn-md btn-primary" to="/resource-center">
				Go to Resource Center
			</Link>
		</div>
	);
};

interface Props {
	area: Area | undefined;
}

const AreaRequestFormEditor: React.FC<Props> = ({ area }) => {
	const context = useItemProviderContext<FormValues>();

	if (!context?.state) throw new Error('Error context');

	const { state: { loading, error } } = context;

	const gender = [
		{ value: 1, label: 'Male' },
		{ value: 2, label: 'Female' },
	];
	const aftercareOptions = [
		{ value: 0, label: 'Private Cremation' },
		{ value: 1, label: 'Communal Cremation' },
		{ value: 2, label: 'Home Burial' },
		{ value: 3, label: 'Family to handle aftercare' },
		{ value: 4, label: 'Unsure at this time' },
	];

	const stateListOptions: Array<ListOption> = [];
	const sourcesListOptions: Array<{ value: number; label: string }> = [];
	const [stateList, setStateList] = React.useState<Array<StatesListItem>>([]);
	const [species, setSpecies] = React.useState<Array<PetSpecies>>([]);
	const [ailments, setAilments] = React.useState<Array<Ailments>>([]);
	const [formSuccess, setFormSuccess] = React.useState<FormResponse>({ success: false, error: '' });
	const [sources, setSources] = React.useState<Array<Sources>>([]);
	const [attachment, setAttachment] = React.useState<Array<FileInterface>>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'png', 'jfif'];
	const maxImgCount = 1;

	const transformSources = () => {
		sources && (
			sources.map((item, index) => {
				sourcesListOptions.push({ value: item.id, label: item.name });
			})
		);
	};

	transformStateList(stateList, stateListOptions);

	const requestSources = () => {
		rest.get<List<Sources>>('v1/sources').then((res) => setSources(res.list));
	};
	transformSources();

	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states')
			.then((res) => setStateList(res.list));

		rest.get<List<Ailments>>('v1/ailments')
			.then((res) => {
				const ailmentsSorted = res.list.sort((a, b) => (a.name > b.name ? 1 : -1)).filter((item) => item.name !== 'Other');
				setAilments(ailmentsSorted);
			});

		rest.get<List<PetSpecies>>('v1/petspecies')
			.then((res) => setSpecies(res.list));
	}, []);

	if (!area) {
		return null;
	}

	return !formSuccess.success ? (
		<div className="area-request-form">
			<h1 className="area__title">
				{area.name}
				<br />
				Information Request Form
			</h1>
			<MiddleText
				title=""
				// eslint-disable-next-line max-len
				text="Lap of Love is here to help you and your pet with in-home euthanasia, veterinary hospice, telehospice, and mobile end-of-life care services. Please complete the form below and a Veterinary Care Coordinator will be in touch soon."
			/>
			<ItemEditor<FormValues>
				formikProps={{
					initialValues,
					validationSchema,
				}}
				showMessages={false}
				readonly={false}
				withButtons={false}
				beforeSubmit={(values, actions, submit) => {
					values.areaId = area.id;
					submit();
				}}
				afterSubmit={() => {
					setFormSuccess({ success: true, error: '' });
					pushEvent('form_submit', { formName: 'Area-Form' });
				}}
				customButtons={(item, formikBag) => (
					<div className="area-request-form-group area-request-form-group_submit">
						<button type="submit" className="btn btn-md btn-primary" disabled={loading}>
							Submit
						</button>
						{Object.values(formikBag.errors).some((el) => el) && formikBag.submitCount > 0 && (
							<div className="submit-validate-error">Please fill in all required fields marked with an asterisk</div>
						)}
						{loading && <BounceLoader />}
					</div>
				)}
				edit={(formikBag) => (
					<>
						<MiddleText title="Contact information" text="" />
						<div className="area-request-form-group">
							<CustomInput name="firstName" placeholder="First Name*" formikBag={formikBag} />
							<CustomInput name="lastName" placeholder="Last Name*" formikBag={formikBag} />
							<CustomInput name="email" placeholder="Email Address*" formikBag={formikBag} trimValue />
							<div className="phone">
								<FormikField
									title="Phone Number"
									fieldName="phone"
									render={(fieldProps) => <CustomPhoneControl placeholder="Phone Number*" fieldProps={fieldProps} />}
								/>
							</div>
						</div>
						<div className="area-request-form-group">
							<CustomInput name="streetLine1" placeholder="Street Address*" formikBag={formikBag} />
							<CustomInput name="streetLine2" placeholder="Street Address Line 2" formikBag={formikBag} />
							<CustomInput name="city" placeholder="City*" formikBag={formikBag} />
							<SearchFieldAuto
								name="state"
								placeholder="State*"
								listOptions={stateListOptions}
								formikBag={formikBag}
								onSelect={(value, option) => {
									formikBag.setFieldValue('state', value);
								}}
								onChange={(value) => {
									formikBag.setFieldValue('state', value);
								}}
								searchButton={false}
								adjustValue
								customFilter={stateFilter}
							/>
							<CustomInput name="zip" placeholder="US Zip Code*" formikBag={formikBag} />
						</div>
						<div className="area-request-form-group placeholder-smaller-size">
							<CustomSelect
								name="source"
								placeholder="How did you hear about Lap of Love?"
								onSelect={(value, option) => {
									formikBag.setFieldValue('source', value);
								}}
								onChange={(value, option) => {
									formikBag.setFieldValue('source', value);
								}}
								options={sourcesListOptions}
								showSearch={false}
								onFocus={requestSources}
								transform={false}
								formikBag={formikBag}
							/>
						</div>

						<MiddleText title="Pet information" text="" />
						<div className="area-request-form-group">
							<CustomInput name="petName" placeholder="Pet's Name*" formikBag={formikBag} />
							<CustomSelect
								name="petSpecies"
								placeholder="Pet's Species*"
								onSelect={(value, option) => {
									formikBag.setFieldValue('petSpecies', value);
								}}
								onChange={(value, option) => {
									formikBag.setFieldValue('petSpecies', value);
								}}
								options={species}
								showSearch={false}
								onFocus={() => {}}
								transform
								formikBag={formikBag}
							/>

							<CustomSelect
								name="gender"
								placeholder="Gender*"
								onSelect={(value, option) => {
									formikBag.setFieldValue('gender', value);
								}}
								onChange={(value, option) => {
									formikBag.setFieldValue('gender', value);
								}}
								options={gender}
								showSearch={false}
								onFocus={() => {}}
								formikBag={formikBag}
							/>

							<CustomInput type="number" name="petAge" placeholder="Pet's Age*" formikBag={formikBag} innerIcon="Yrs" />
							<CustomInput type="number" name="petWeight" placeholder="Pet's Weight*" formikBag={formikBag} innerIcon="Lbs" />
						</div>

						<div className="area-request-form-group">
							<div className="full-height-content">
								<h5>What is your pet's main ailment?</h5>
								<CustomSelect
									name="ailment"
									onSelect={(value, option) => {
										formikBag.setFieldValue('ailment', value);
									}}
									onChange={(value, option) => {
										formikBag.setFieldValue('ailment', value);
									}}
									options={ailments}
									showSearch
									onFocus={() => {}}
									transform
									formikBag={formikBag}
								/>
							</div>
							<div className="full-height-content">
								<h5>What type of aftercare would you prefer?</h5>
								<CustomSelect
									name="aftercare"
									onSelect={(value, option) => {
										formikBag.setFieldValue('aftercare', value);
									}}
									onChange={(value, option) => {
										formikBag.setFieldValue('aftercare', value);
									}}
									options={aftercareOptions}
									showSearch={false}
									onFocus={() => {}}
									formikBag={formikBag}
								/>
							</div>
						</div>
						<div className="area-request-form-group area-request-form-group_textarea">
							<CustomInput
								component="textarea"
								name="comment"
								placeholder="Any additional comments or concerns?"
								formikBag={formikBag}
							/>
						</div>
						<div className="area-request-form-group">
							<Field
								name="filePath"
							>
								{(field) => {
									return (
										<div
											className={
												field.form.errors.filePath && field.form.touched.filePath
													? 'attachment modal-wrap attachment_cropped'
													: 'attachment modal-wrap'
											}
										>
											<h4 className="attachment-title">Would you like to send us an image of your pet?</h4>
											<AttachmentPreview attachment={attachment} setAttachment={setAttachment} formikBag={formikBag} />
											<div className="attachment-upload-container">
												<CustomFile
													update={(data) => onFileUpload(
														data,
														field,
														formikBag,
														'filePath',
														maxFile,
														imgExtension,
														attachment,
														maxImgCount,
														setAttachment,
													)}
													type="jobInquiry"
													onUploadType="jobInquiry"
													fileType={FileType.File}
													objectId={-1}
													noRelation
													asButton
													buttonClassName="btn btn-secondary"
													buttonCaption="Upload a file"
													multiple
												/>
												<span className="attachment-note">(Maximum file size is 10MB in JPG, JFIF, or PNG format)</span>
											</div>
											{field.form.errors.filePath && field.form.touched.filePath ? (
												<div className="custom-input__error error_top-gap">
													{' '}
													{field.form.errors.filePath}
												</div>
											) : null}
										</div>
									);
								}}
							</Field>
						</div>
						<div className="area__news">
							By providing a telephone number and submitting the form you are consenting to be contacted by SMS text message by Lap
							of Love. Message & data rates may apply. Reply STOP to opt out of further messaging.
						</div>
						<div className="area-request-form-group area-request-form-group_recaptcha">
							<FormikField
								containerClassName="captcha"
								fieldName="captcha"
								render={() => <CustomReCAPTCHA formikBag={formikBag} />}
							/>
						</div>

						<div className="area-request-form-group area-request-form-group_submit">
							{error.length ? <div className="submit-validate-error">Service unavailable. Please try again later</div> : null}
						</div>
					</>
				)}
			/>
		</div>
	) : (
		<FormSuccess />
	);
};

const AreaRequestForm: React.FC<Props> = ({ area }) => {
	return (
		<CustomRequestProvider requestMethod="POST">
			<ItemProvider<FormValues>
				id={-1}
				type="petregistration"
				saveRequest="petregistration"
			>
				<AreaRequestFormEditor area={area} />
			</ItemProvider>
		</CustomRequestProvider>
	);
};

export default AreaRequestForm;
